import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** A full-size hidden copy reserves every line; assistive tech reads the text once. */
export default function ArchitectureDescription({ text, reduced, active = true }: {
  text: string; reduced: boolean; active?: boolean;
}) {
  const [count, setCount] = useState(0);
  const inkRef = useRef<HTMLSpanElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const ink = inkRef.current;
    const cursor = cursorRef.current;
    if (!ink || !cursor) return;
    const place = () => {
      const textNode = ink.querySelectorAll(".ah-type-letter")[Math.max(0, count - 1)]?.firstChild;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
      const range = document.createRange();
      range.setStart(textNode, count ? textNode.textContent?.length ?? 0 : 0);
      range.collapse(true);
      const caret = range.getBoundingClientRect();
      const bounds = ink.getBoundingClientRect();
      cursor.style.transform = `translate(${caret.left - bounds.left + 3}px,${caret.top - bounds.top + 3}px)`;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(ink);
    return () => observer.disconnect();
  }, [count]);
  useEffect(() => {
    if (reduced || !active) return;
    let frame = 0;
    let elapsed = 0;
    let previous = performance.now();
    const tick = (now: number) => {
      if (!document.hidden) elapsed += now - previous;
      previous = now;
      const next = Math.min(text.length, Math.max(0, Math.floor((elapsed - 300) / 18)));
      setCount(next);
      if (next < text.length) frame = requestAnimationFrame(tick);
    };
    const visibility = () => { previous = performance.now(); };
    document.addEventListener("visibilitychange", visibility);
    frame = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(frame); document.removeEventListener("visibilitychange", visibility); };
  }, [text, reduced, active]);
  const length = reduced || !active ? text.length : count;
  return <span className="ah-typed">
    <span className="sr-only">{text}</span>
    <span className="ah-typed-reserve" aria-hidden="true">{text}</span>
    <span className="ah-typed-ink" ref={inkRef} aria-hidden="true">{Array.from(text).map((letter, index) =>
      <span className="ah-type-letter" key={index} style={{ opacity: index < length ? 1 : 0 }}>{letter}</span>)}
      {length < text.length && <span className="ah-type-cursor" ref={cursorRef} />}
    </span>
  </span>;
}
